/**
 * Derive the headline "is this shop's inventory connection healthy?" signal
 * for the Inventory tab's ConnectionStatusCard.
 *
 * The card was previously always green ("Connected · Live") regardless of how
 * long it had been since the last ingest, so retailers didn't know when the
 * feed had stopped flowing (NEA-5921).
 *
 * One blocker today:
 *   - `no-recent-upload` — last ingest is older than DISCONNECTED_THRESHOLD_DAYS.
 *     POS connectivity / scheduling issue on their side.
 *
 * Deliberately NOT surfaced on this card:
 *   - Stock-file errors (invalid prices, bad barcodes, etc.) — covered by the
 *     `FeedIssues` + `ProfileCompleteness` sections lower on the page.
 *   - Google disapprovals — covered by `ChannelSyncStatus` on the Google
 *     channel page. Flagging Google issues on the *inventory connection*
 *     card conflated two different surfaces (ingest vs channel).
 *
 * The 3-day threshold sits below the global stale-ingest banner (7/14/30
 * days in `useHealthBanners.js`) so the inline card flags the problem first,
 * then the global banner escalates if it stays unresolved.
 *
 * At 14 days (`RECONNECT_THRESHOLD_DAYS`, matching the banner's `warning`
 * severity) the feed is treated as frozen rather than merely late: the card's
 * CTA switches to the reconnect flow and the visibility stats stop claiming
 * the catalogue is live (NEA-5887 / NEA-5934). `needsReconnect` is the shared
 * signal for both surfaces.
 */

export const DISCONNECTED_THRESHOLD_DAYS = 3;
export const RECONNECT_THRESHOLD_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function daysSince(iso) {
	if (!iso) return null;
	const t = Date.parse(iso);
	if (!Number.isFinite(t)) return null;
	return Math.floor((Date.now() - t) / MS_PER_DAY);
}

export function deriveConnectionState({ source } = {}) {
	if (!source) {
		return { status: 'connected', blocker: null, daysSinceSync: null, needsReconnect: false };
	}

	const daysSinceSync = daysSince(source.lastSyncAt);

	if (daysSinceSync != null && daysSinceSync >= DISCONNECTED_THRESHOLD_DAYS) {
		return {
			status: 'disconnected',
			blocker: { kind: 'no-recent-upload', days: daysSinceSync },
			daysSinceSync,
			needsReconnect: daysSinceSync >= RECONNECT_THRESHOLD_DAYS
		};
	}

	return { status: 'connected', blocker: null, daysSinceSync, needsReconnect: false };
}
